import type { FeatureKey, PipelineCheckResult } from '../types';
import { PIPELINE_SPEC } from './constants';
import type { VerificationEvidence } from './buildVerificationEvidence';
import {
  formatVerificationFindings,
  formatVerificationSummary,
} from './formatVerificationCopy';

const minValidPercent = (values: Record<FeatureKey, number>) =>
  Math.min(...Object.values(values));

export const buildLocalVerificationReport = (
  evidence: VerificationEvidence,
  deterministicChecks: PipelineCheckResult[],
  deterministicPassed: boolean
): {
  status: 'verified' | 'warning';
  summary: string;
  findings: string[];
} => {
  const failedChecks = deterministicChecks.filter(check => !check.passed);
  const includedCount = evidence.includedSessions.length;

  if (!deterministicPassed) {
    return {
      status: 'warning',
      summary: `La verifica numerica ha rilevato ${failedChecks.length} controlli da rivedere sulle ${includedCount} sessioni incluse.`,
      findings: formatVerificationFindings(
        failedChecks.slice(0, 6).map(check => check.message)
      ),
    };
  }

  const sessionIds = evidence.includedSessions.map(item => item.sessionId);
  const minCoverage = evidence.includedSessions.reduce((min, item) => {
    const sessionMin = minValidPercent(item.quality.validDataPercentByFeature);
    return Math.min(min, sessionMin);
  }, 100);

  const repairedSources = evidence.includedSessions.filter(
    item => item.hrDataSource === 'hrData_repaired'
  );
  const originalSources = evidence.includedSessions.filter(
    item => item.hrDataSource === 'hrData'
  );

  const findings = formatVerificationFindings([
    `Qualità dati: ${includedCount} sessioni incluse (${sessionIds.join(', ')}); copertura minima ${minCoverage.toFixed(1)}% (soglia ${PIPELINE_SPEC.minValidPercent}%).`,
    `Preprocessing: conteggi coerenti su allineamento, deduplicazione e gestione gap; nessuna anomalia numerica rilevata.`,
    `Segmenti pre-VR e post-VR: tutti validi da ${PIPELINE_SPEC.segmentDurationSeconds}s (5 minuti) sulle sessioni incluse.`,
    `Analisi spettrale: passa-basso 0.5 Hz e analisi Welch applicati come da protocollo BECOME.`,
    repairedSources.length === 0
      ? 'Origine tracciato: tutte le sessioni usano il tracciato originale.'
      : originalSources.length === 0
        ? `Origine tracciato: tutte le sessioni (${repairedSources.map(item => item.sessionId).join(', ')}) usano il tracciato riparato.`
        : `Origine tracciato: sessioni ${repairedSources.map(item => item.sessionId).join(', ')} con tracciato riparato; sessioni ${originalSources.map(item => item.sessionId).join(', ')} con tracciato originale.`,
    evidence.groupSummary.outlierCount > 0
      ? `Outlier: ${evidence.groupSummary.outlierCount} sessione/i segnalate come outlier di gruppo (da valutare in interpretazione).`
      : `Outlier: nessuna sessione segnalata come outlier di gruppo.`,
  ]);

  const summary = formatVerificationSummary(
    `Coerenza globale della pipeline di normalizzazione spettrale con controlli numerici tutti superati; qualità dati e parametri conformi al protocollo BECOME.`,
    { deterministicPassed: true, includedCount }
  );

  return {
    status: 'verified',
    summary,
    findings,
  };
};
